import React, { Suspense, lazy } from 'react';
import './App.css';
import HomePage from './screens/HomePage';
import NotFound from './screens/NotFound';
import Login from './screens/Login';
import { BrowserRouter as Router, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap/dist/js/bootstrap.min.js';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './config/queryClient';
import Carta from './screens/Carta';
import Menu from './components/Menu/Menu';
import PrivateRoute from './components/PrivateRoute';
import { ConfigProvider } from './context/ConfigContext';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const StockSetup = lazy(() => import('./screens/StockSetup'));
const StockGuide = lazy(() => import('./screens/StockGuide'));
const PriceListsGuide = lazy(() => import('./screens/PriceListsGuide'));

const MisProductos = lazy(() => import('./screens/MisProductos'));
const ExpirationsProduct = lazy(() => import('./screens/ExpirationsProduct'));
const Categories = lazy(() => import('./screens/Categories'));
const SocialMedia = lazy(() => import('./screens/SocialMedia'));
const CartaOrderShop = lazy(() => import('./screens/CartaOrderShop'));
const PedidosCaja = lazy(() => import('./screens/PedidosCaja'));
const Orders = lazy(() => import('./screens/Orders'));
const ShopPage = lazy(() => import('./screens/ShopPage'));
const ReporteProductos = lazy(() => import('./screens/ReporteProductos'));
const ReporteVentas = lazy(() => import('./screens/ReporteVentas'));
const Stock = lazy(() => import('./screens/Stock'));
const StockPurchaseDocument = lazy(() => import('./screens/StockPurchaseDocument'));
const StockLocations = lazy(() => import('./screens/StockLocations'));
const StockMovements = lazy(() => import('./screens/StockMovements'));
const StatusOrderView = lazy(() => import('./screens/StatusOrderView'));
const Configuraciones = lazy(() => import('./screens/Configuraciones'));
const ConfiguracionesSistema = lazy(() => import('./screens/ConfiguracionesSistema'));
const ClientList = lazy(() => import('./screens/ClientList'));
const InvoiceGenerator = lazy(() => import('./components/invoice/invoiceGenerator'));
const Brands = lazy(() => import('./screens/Brands'));
const PriceLists = lazy(() => import('./screens/PriceLists'));
const Movement = lazy(() => import('./screens/Movement'));
const RecurringMovements = lazy(() => import('./screens/RecurringMovements'));
const ReporteMovements = lazy(() => import('./screens/ReporteMovements'));
const Users = lazy(() => import('./screens/Users'));
const Providers = lazy(() => import('./screens/Providers'));
const Promociones = lazy(() => import('./screens/Promociones'));
const PriceLabels = lazy(() => import('./screens/PriceLabels'));
const PointsOfSale = lazy(() => import('./screens/PointsOfSale'));
const CashRegisterDashboard = lazy(() => import('./screens/CashRegisterDashboard'));
const CashRegisterOpen = lazy(() => import('./screens/CashRegisterOpen'));
const CashRegisterClose = lazy(() => import('./screens/CashRegisterClose'));
const CashRegisterHistory = lazy(() => import('./screens/CashRegisterHistory'));
const CurrentAccountProviders = lazy(() => import('./screens/CurrentAccountProviders'));
const CurrentAccountClients = lazy(() => import('./screens/CurrentAccountClients'));
const ProviderAccountMovements = lazy(() => import('./screens/ProviderAccountMovements'));
const ClientAccountMovements = lazy(() => import('./screens/ClientAccountMovements'));
const ProviderOrders = lazy(() => import('./screens/ProviderOrders'));
const ProviderOrderDetail = lazy(() => import('./screens/ProviderOrderDetail'));
const ProviderOrderFinalizeReceipt = lazy(() => import('./screens/ProviderOrderFinalizeReceipt'));
const ProviderOrderPending = lazy(() => import('./screens/ProviderOrderPending'));

const RouteFallback = () => (
  <div className="d-flex justify-content-center align-items-center p-5">
    <div className="spinner-border text-primary" role="status">
      <span className="visually-hidden">Cargando...</span>
    </div>
  </div>
);

const withAuth = (element) => <PrivateRoute>{element}</PrivateRoute>;

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <ToastContainer />
        <RoutesWithMenu />
      </Router>
    </QueryClientProvider>
  );
}

function isMainDomain(hostname) {
  const hostOnly = hostname.split(':')[0];
  if (hostOnly === 'localhost') return true;

  if (
    hostname === 'qreate-demo.ucoode.com.ar' ||
    hostname === 'qreate-dev.ucoode.com.ar' ||
    hostname === 'qreate.ucoode.com.ar'
  ) return true;

  return false;
}

function isSubdomain(hostname) {
  const hostOnly = hostname.split(':')[0];
  if (hostOnly.endsWith('.localhost')) return true;

  if (
    hostname.match(/^[^.]+-demo\.ucoode\.com\.ar$/) ||
    hostname.match(/^[^.]+-dev\.ucoode\.com\.ar$/) ||
    hostname.match(/^[^.]+-qreate\.ucoode\.com\.ar$/)
  ) return true;

  return false;
}

function RoutesWithMenu() {
  const location = useLocation();
  const hostname = window.location.hostname + (window.location.port ? `:${window.location.port}` : '');

  if (isMainDomain(hostname)) {
    const noMenuRoutes = ['/', '/carta', '/estadodelpedido', '/notfound'];
    const showMenu = !noMenuRoutes.includes(location.pathname.toLocaleLowerCase());

    return (
      <ConfigProvider>
        {showMenu && <Menu />}
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<Login />} />
            <Route path="/estadoDelPedido" element={<StatusOrderView />} />
            <Route path="/pedidoMostrador" element={withAuth(<CartaOrderShop />)} />
            <Route path="/pedidosCaja" element={withAuth(<PedidosCaja />)} />
            <Route path="/home" element={withAuth(<HomePage />)} />
            <Route path="/shop" element={withAuth(<ShopPage />)} />
            <Route path="/users" element={withAuth(<Users />)} />
            <Route path="/providers" element={withAuth(<Providers />)} />
            <Route path="/provider-accounts" element={withAuth(<CurrentAccountProviders />)} />
            <Route path="/provider-account-movements" element={withAuth(<ProviderAccountMovements />)} />
            <Route path="/client-accounts" element={withAuth(<CurrentAccountClients />)} />
            <Route path="/client-account-movements" element={withAuth(<ClientAccountMovements />)} />
            <Route path="/misProductos" element={withAuth(<MisProductos />)} />
            <Route path="/expirationsProduct" element={withAuth(<ExpirationsProduct />)} />
            <Route path="/categories" element={withAuth(<Categories />)} />
            <Route path="/marcas" element={withAuth(<Brands />)} />
            <Route path="/personalizacion" element={withAuth(<SocialMedia />)} />
            <Route path="/orders" element={withAuth(<Orders />)} />
            <Route path="/reporteProductos" element={withAuth(<ReporteProductos />)} />
            <Route path="/reporteMovimientos" element={withAuth(<ReporteMovements />)} />
            <Route path="/reporteVentas" element={withAuth(<ReporteVentas />)} />
            <Route path="/config" element={<Navigate to="/config/carta" replace />} />
            <Route path="/config/carta" element={withAuth(<Configuraciones />)} />
            <Route path="/config/sistema" element={withAuth(<ConfiguracionesSistema />)} />
            <Route path="/stock" element={withAuth(<Stock />)} />
            <Route path="/stock/comprobante-compra" element={withAuth(<StockPurchaseDocument />)} />
            <Route path="/stock/guia" element={withAuth(<StockGuide />)} />
            <Route path="/stock/setup" element={withAuth(<StockSetup />)} />
            <Route path="/stockLocations" element={withAuth(<StockLocations />)} />
            <Route path="/stockMovements" element={withAuth(<StockMovements />)} />
            <Route path="/client" element={withAuth(<ClientList />)} />
            <Route path="/invoice" element={withAuth(<InvoiceGenerator />)} />
            <Route path="/price-lists" element={withAuth(<PriceLists />)} />
            <Route path="/price-lists/guia" element={withAuth(<PriceListsGuide />)} />
            <Route path="/promotions" element={withAuth(<Promociones />)} />
            <Route path="/price-labels" element={withAuth(<PriceLabels />)} />
            <Route path="/movements" element={withAuth(<Movement />)} />
            <Route path="/recurring-movements" element={withAuth(<RecurringMovements />)} />
            <Route path="/points-of-sale" element={withAuth(<PointsOfSale />)} />
            <Route path="/cash-register" element={withAuth(<CashRegisterDashboard />)} />
            <Route path="/cash-register/open" element={withAuth(<CashRegisterOpen />)} />
            <Route path="/cash-register/close" element={withAuth(<CashRegisterClose />)} />
            <Route path="/cash-register/history" element={withAuth(<CashRegisterHistory />)} />
            <Route path="/pedidos-proveedores" element={withAuth(<ProviderOrders />)} />
            <Route path="/pedidos-proveedores/pendientes" element={withAuth(<ProviderOrderPending />)} />
            <Route path="/pedidos-proveedores/:id" element={withAuth(<ProviderOrderDetail />)} />
            <Route path="/pedidos-proveedores/:id/finalizar-recepcion" element={withAuth(<ProviderOrderFinalizeReceipt />)} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </ConfigProvider>
    );
  }

  if (isSubdomain(hostname)) {
    return (
      <ConfigProvider>
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<Carta />} />
            <Route path="/estadoDelPedido" element={<StatusOrderView />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </ConfigProvider>
    );
  }

  return <NotFound />;
}

export default App;
